import outlookExampleImg from "@assets/HarmCheck_Outlook_Example_Website@4x_1776704289013.webp";
import outlookCitiExampleImg from "@assets/HarmCheck_Outlook_Citi_Example_1777497100587.webp";
import dashboardImg from "@assets/HarmCheck_Rapid_Deploy_Dashboard@4x_1776696809900.webp";
import dashboardImg2 from "@assets/HarmCheck_Rapid_Deploy_Dashboard_2@4x_1776698750350.webp";
import annotatedPdfImg from "@assets/HarmCheck_Rapid_Deploy_Annotated_PDF@4x_1776696809896.webp";
import spreadsheetImg from "@assets/HarmCheck_Rapid_Deploy_Spreadsheet@4x_1776696809900.webp";
import restrictedPopupImg from "@assets/restricted_list_pop_up_web_hero_1777410378335.webp";

export type Product = "Active Listening" | "Rapid Deploy" | "Restricted List";

export type CaseStudyStat = { value: string; label: string };

export type CaseStudyMedia = {
  image: string;
  alt: string;
  caption?: string;
};

export type CaseStudySection = {
  heading?: string;
  paragraphs: string[];
  bullets?: string[];
  media?: CaseStudyMedia;
};

export type CaseStudyQuote = {
  text: string;
  attribution: string;
  role: string;
};

export type CaseStudy = {
  slug: string;
  product: Product;
  industry: string;
  client: string;
  publishedDate: string;
  card: {
    eyebrow: string;
    title: string;
    summary: string;
    outcome: string;
  };
  hero: {
    eyebrow: string;
    headline: string;
    subhead: string;
  };
  stats: CaseStudyStat[];
  challenge: CaseStudySection;
  solution: CaseStudySection;
  results: CaseStudySection;
  quote?: CaseStudyQuote;
};

export const caseStudies: CaseStudy[] = [
  {
    slug: "what-if-citigroup-active-listening",
    product: "Active Listening",
    industry: "Capital Markets & Investment Banking",
    client: "Illustrative analysis · Lindsey v. Citigroup Global Markets, Inc.",
    publishedDate: "Public-record analysis (S.D.N.Y., Nov. 2023)",
    card: {
      eyebrow: "Illustrative · Active Listening",
      title: "What if Citi used HarmCheck before the Lindsey lawsuit?",
      summary:
        "A 2023 federal complaint alleges years of threats, sexual harassment, and retaliation inside the Equities division. Every category named in the filing is exactly what Active Listening was built to catch — at the moment of draft, before send.",
      outcome: "Threats intercepted. Patterns surfaced. Exposure prevented.",
    },
    hero: {
      eyebrow: "Illustrative Analysis · Active Listening · Capital Markets",
      headline: "What if Citi used HarmCheck?",
      subhead:
        "An illustrative analysis based on Lindsey v. Citigroup Global Markets, Inc. (S.D.N.Y., Nov. 20, 2023). Every example below is drawn directly from the public federal complaint. We map the alleged conduct to the Active Listening classifiers that would have flagged it — at the moment a message is drafted, before it ever leaves the firm.",
    },
    stats: [
      { value: "15 yrs", label: "of alleged misconduct described inside one division" },
      { value: "100s", label: "of abusive messages cited in the federal complaint" },
      { value: "11+", label: "Active Listening categories that would have triggered" },
      { value: "0 sec", label: "lead time HarmCheck needs — flags fire at draft, before send" },
    ],
    challenge: {
      heading: "A “locker room” environment, hiding in plain text.",
      paragraphs: [
        "On November 20, 2023, a 15-year Citi Managing Director filed a federal complaint in the Southern District of New York alleging years of harassment, sexual assault, and retaliation inside the bank’s Global Equities division (Lindsey v. Citigroup Global Markets, Inc., No. 1:23-cv-10166).",
        "The conduct described in the complaint is not subtle. According to the public filing, a former senior executive sent the plaintiff hundreds of abusive messages — explicit threats of violence against her and her children, threats to cut her compensation, threats to sabotage her reputation, and degrading sexualized commentary. The wider environment alleged in the complaint — ranking female employees by appearance, mocking the firm’s harassment trainings, excluding women from client opportunities — extended well beyond a single bad actor.",
        "Every one of those categories — violent threats, retaliation, harassment, hostile environment, quid-pro-quo coercion — is a named Title VII, EEOC, and FINRA risk surface. And every one is something Active Listening reads for, by design.",
      ],
      bullets: [
        "Explicit threats of violence — e.g., “And I am going to set you on fire” (¶6)",
        "Threats targeting family — e.g., “Your kids’ life will be ruined from here on in” (¶6)",
        "Retaliation against compensation and career — e.g., “Taking you down in comp. Hard.” (¶6)",
        "Reputation and reprisal threats — e.g., “I plan to f*** your life… drop something on social media and tag the community.” (¶6)",
        "Sexualized harassment — repeated alleged comments on female employees’ bodies and appearance (¶2, ¶6)",
        "Pattern, not incident — alleged misconduct across 15 years, multiple supervisors, one division",
      ],
    },
    solution: {
      heading: "Every category here is a classifier HarmCheck was built for.",
      paragraphs: [
        "HarmCheck Active Listening sits inside Outlook — reading every draft before it leaves an employee’s keyboard. It runs ~50 proprietary classifiers tuned to regulatory, employment, and conduct risk. The categories alleged in the Lindsey complaint are not edge cases; they are exactly the categories the system was purpose-built to detect.",
        "When language patterns like the ones cited in the complaint hit the system, two things happen: the employee is shown a flag and an explanation of the violation, and is given an opportunity to change the message. If the message is sent without removing that flag, it is held in quarantine and is only released if a compliance admin allows it.",
      ],
      bullets: [
        "Violent Threats — language indicating intent to cause physical harm to a person or their family.",
        "Threats Against Family / Coercion — messages targeting an individual’s children, relatives, or dependents to intimidate or compel behavior.",
        "Retaliation & Coercion — explicit or implicit threats to a person’s compensation, role, reputation, or professional standing in response to a complaint, rejection, or refusal; also language conditioning a workplace benefit, opportunity, or status on sexual or personal compliance, especially in supervisor-to-direct-report communications.",
        "Sexual Harassment / Hostile Environment — sexualized commentary, unwelcome propositions, or degrading remarks based on sex, gender, or sexual orientation.",
        "Restricted-List violations / MNPI — communication of material non-public information, or contact across information barriers maintained on the firm’s restricted list.",
      ],
      media: {
        image: outlookCitiExampleImg,
        alt: "HarmCheck Active Listening flagging threatening sentences in a draft Outlook email before send",
        caption: "Active Listening in Outlook: these are actual messages from the lawsuit that would have never left the firm.",
      },
    },
    results: {
      heading: "What changes when these categories hit a draft, not a courtroom.",
      paragraphs: [
        "Drafts containing violent threats, retaliation language, or sexual harassment are stopped before they leave the firm. The audit record — drafter, recipient, channel, classifier, timestamp — is created in the same moment, so compliance has evidence of intent, escalation, and pattern from day one rather than discovering it years later in a federal complaint.",
        "When patterns repeat across senders or escalate over time, Active Listening surfaces them as conduct risk before they become a 15-year scandal across a trading floor. Investigations that once required outside counsel and Rapid Deploy retrospectively can instead happen in real time, on the firm’s own infrastructure, with the audit trail already built.",
        "That is the precise gap Active Listening closes. The channels themselves become the signal — so compliance gets visibility without depending on a victim being brave enough to walk into HR knowing the people who would retaliate are the same people they would be reporting.",
      ],
      bullets: [
        "Threats and harassment intercepted at the moment of draft — not discovered in litigation discovery",
        "Pattern of misconduct surfaced as a single conduct-risk view across senders, channels, and time",
        "Audit-ready record for every flagged draft — original message, triggering sentence, category, classifier version, reviewer action",
        "Hundreds of millions in potential settlement, regulatory, and reputational exposure addressed before it ever lands in a federal docket",
      ],
      media: {
        image: dashboardImg2,
        alt: "Active Listening conduct-risk dashboard showing categorized incidents, severity, and pattern view across senders and time",
        caption: "View of the flagged sentences in the Rapid Deploy dashboard.",
      },
    },
    quote: {
      text: "Women… reasonably understood that if they complained, the Bank would retaliate against them, because senior managers were involved in making and condoning these harassing comments.",
      attribution: "Lindsey v. Citigroup Global Markets, Inc.",
      role: "S.D.N.Y. Complaint, ¶44 (Nov. 20, 2023) — public record",
    },
  },

  {
    slug: "amlaw-100-rapid-deploy-deposition-prep",
    product: "Rapid Deploy",
    industry: "Fair Housing Litigation",
    client: "A Prominent Law Firm",
    publishedDate: "February 2026",
    card: {
      eyebrow: "Rapid Deploy",
      title: "Fair housing case: 246,000 documents reviewed in six days.",
      summary:
        "In a fair housing suit against a city council accused of blocking a housing development, discovery produced three years of communications. Rapid Deploy ran the corpus at 20,000+ pages per hour — surfacing coded NIMBY language and pretext patterns with sentence-level citations.",
      outcome: "Pattern documented. Citations sourced. Depositions ready.",
    },
    hero: {
      eyebrow: "Rapid Deploy • Fair Housing Litigation",
      headline: "246,000 documents. Six days. A discrimination pattern, surfaced sentence by sentence.",
      subhead:
        "A Prominent Law Firm was preparing depositions in a fair housing case against a city council and its individual members accused of blocking a housing development for discriminatory reasons. Discovery had produced three years of council communications. Manual review estimates ran six weeks. They had one.",
    },
    stats: [
      { value: "246,000", label: "documents processed end-to-end" },
      { value: "20,000+/hr", label: "pages throughput on the firm’s own infrastructure" },
      { value: "6 days", label: "from corpus delivery to deposition-ready report" },
      { value: "9", label: "fair-housing risk categories surfaced across the corpus" },
    ],
    challenge: {
      heading: "One week to depositions. Three years of council members’ communications to review.",
      paragraphs: [
        "The firm represented the plaintiffs in a fair housing case against a city council and its individual members, accused of blocking a proposed housing development through zoning denials, procedural delays, and pretextual objections motivated by the race and family composition of the residents the development was expected to serve. Discovery produced 246,000 documents — emails, internal city memos, text messages, and constituent communications — covering a three-year window across council members and city staff.",
        "Defense counsel had been reviewing for weeks. Manual review of the full corpus would take a 25-attorney team approximately six weeks at standard rates. The litigation team had one week before the first depositions of council members and senior city staff. Anything missed would mean walking into a deposition without the document the witness was about to be asked to authenticate.",
      ],
      bullets: [
        "246,000 documents across email, internal memos, text messages, and constituent communications",
        "Three-year window across council members and city staff",
        "First depositions of council members and senior city staff in 7 days",
        "Manual review estimate: $1.4M and six weeks",
      ],
    },
    solution: {
      heading: "HarmCheck ran the corpus at 20,000+ pages per hour.",
      paragraphs: [
        "The firm pointed HarmCheck Rapid Deploy at the document corpus on Sunday evening. By Tuesday morning, the system had completed a full pass across all 246,000 documents — surfacing communications referencing protected classes, coded references to “neighborhood character” and “the wrong kind of resident,” and the pretext patterns documented in prior fair housing matters against municipal defendants.",
        "Every hit was returned with the original document, the precise sentence triggering the classification, the category, and a confidence score. Senior associates worked from a curated subset of ~4,200 documents — a 98.3% reduction in manual review burden — backed by a complete audit trail of what had been reviewed, what had been excluded, and why.",
      ],
      media: {
        image: spreadsheetImg,
        alt: "Rapid Deploy spreadsheet output showing per-document classifications, severity scores, and source citations",
        caption: "Flagged sentences are delivered in a dashboard, organized by category, and attributions are linked to the law firm’s case management system.",
      },
    },
    results: {
      heading: "Annotated PDFs. Findings spreadsheet. Deposition-ready evidence.",
      paragraphs: [
        "By the end of day six, the litigation team had a curated set of ~4,200 flagged statements to review — each tied back to the source document, the precise triggering sentence, and the category that surfaced it. The judgment about what mattered, what to use, and how to use it stayed with the attorneys.",
        "Instead of a 25-attorney team spending six weeks combing through 246,000 documents at standard rates, the litigation team spent six days reviewing a focused subset — with citations, source links, and category labels already in front of them. HarmCheck didn’t write the deposition outline, develop the case theory, or decide what to put in front of a witness. It just made sure the right statements were on the table in time for the team to do that work.",
      ],
      bullets: [
        "~4,200 flagged statements surfaced for attorney review out of a 246,000-document corpus",
        "Each flag linked back to its source document and the exact triggering sentence, grouped by category",
        "Annotated PDFs and a master findings spreadsheet for attorneys to work from in deposition prep",
        "Thousands of hours and an estimated $1.2M in manual review saved versus a traditional eDiscovery workflow",
      ],
      media: {
        image: annotatedPdfImg,
        alt: "Annotated PDF output from Rapid Deploy with highlighted passages and per-sentence category labels",
        caption: "Annotated PDF output. Every highlight is sourced, scored, and attributable.",
      },
    },
    quote: {
      text: "We walked into that deposition having read every document that mattered. Six days for what would have been a six-week review — it saved us thousands of hours of manual review.",
      attribution: "Litigation Partner",
      role: "A Prominent Law Firm",
    },
  },

  {
    slug: "investment-bank-restricted-list-mnpi",
    product: "Restricted List",
    industry: "Capital Markets & Investment Banking",
    client: "Bulge-Bracket Investment Bank",
    publishedDate: "January 2026",
    card: {
      eyebrow: "Restricted List",
      title: "Bulge-bracket bank stops an MNPI leak before send.",
      summary:
        "An equity research analyst began drafting a market commentary referencing a name on the firm’s restricted list. Restricted List intercepted the message, blocked the send, and routed it for compliance review — before any external party saw it.",
      outcome: "Zero MNPI breach. Zero regulatory disclosure. Zero headline.",
    },
    hero: {
      eyebrow: "Restricted List • Capital Markets",
      headline: "MNPI never leaves the building.",
      subhead:
        "HarmCheck Restricted List is the last line of defense against MNPI leaks, insider trading exposure, and conflict-of-interest violations. Here’s what happens when it works exactly as designed.",
    },
    stats: [
      { value: "<200ms", label: "from draft trigger to interception" },
      { value: "100%", label: "of messages referencing restricted names quarantined" },
      { value: "0", label: "regulatory disclosures filed against the firm" },
      { value: "94", label: "active issuers monitored across the firm’s restricted list" },
    ],
    challenge: {
      heading: "MNPI risk lives in the seconds before send.",
      paragraphs: [
        "Investment banks live with a structural conflict: bankers working on confidential transactions sit a few floors away from research analysts publishing public commentary, traders executing client orders, and salespeople pitching ideas. The information barrier between them is both a regulatory requirement and an operational nightmare.",
        "Periodic training and quarterly attestations are not enough. The risk surfaces in the moment a single analyst, in good faith, drafts a paragraph that touches a name they didn’t know was on the restricted list. By the time legal sees the email, it has already left the building.",
      ],
      bullets: [
        "94 active restricted names spanning M&A, IPO, and confidential mandates",
        "1,200+ analysts, traders, and salespeople with daily client communication",
        "Single MNPI breach historically equals 7-figure regulatory exposure plus reputational damage",
      ],
    },
    solution: {
      heading: "Restricted List intercepts at the keystroke level.",
      paragraphs: [
        "The bank deployed Restricted List directly into Outlook, drawing from the company-managed restricted list as deal teams added and removed names.",
        "When an analyst began drafting external research that referenced a restricted issuer, the system intercepted the draft before send, displayed an explanation tying back to the firm’s information barrier policy, and routed the message to compliance for review. The analyst was never blamed — the system simply prevented the breach from ever happening.",
      ],
      media: {
        image: restrictedPopupImg,
        alt: "Restricted List interception popup inside Outlook showing the blocked send and the policy explanation",
        caption: "The employee is given a chance to catch the mistake. If the employee hits send anyway, the message is quarantined and routed for compliance review.",
      },
    },
    results: {
      heading: "Breach prevented. Audit trail created. Regulator never called.",
      paragraphs: [
        "In the twelve months since deploying Restricted List, the bank has intercepted 387 communications referencing restricted names — every one of them caught before the message left the firm. None resulted in MNPI disclosure. None triggered a regulatory matter. None became a headline.",
        "Equally important, every interception generated a complete audit record: which name was triggered, which policy was applied, which compliance officer reviewed the message, and what the disposition was. When the firm’s annual examination came around, the controls demonstrated themselves.",
      ],
      bullets: [
        "387 interceptions in the first 12 months — zero leaks",
        "Sub-200ms response time, invisible to the user until the policy fires",
        "Real-time sync with the firm’s authoritative restricted-name source",
        "Complete audit log delivered into compliance’s case management system",
      ],
      media: {
        image: dashboardImg2,
        alt: "Compliance dashboard showing aggregate Restricted List interceptions, dispositions, and reviewer activity",
        caption: "Aggregate compliance view. Every interception, reviewer, and disposition — in one place.",
      },
    },
    quote: {
      text: "We went from training-and-hope to a system that prevents the breach. The first time it caught one, we knew the entire investment paid for itself.",
      attribution: "Head of Compliance, Capital Markets",
      role: "Bulge-Bracket Investment Bank",
    },
  },
];

export function getCaseStudyBySlug(slug: string): CaseStudy | undefined {
  return caseStudies.find((c) => c.slug === slug);
}
